/**
 * Environment-aware Okta Configuration
 * 
 * Detects the current environment from the hostname and returns
 * the appropriate Okta configuration for that environment.
 * 
 * Environments:
 * - visiondev.echostar.com  → Development
 * - visiontest.echostar.com → Test
 * - visionint.echostar.com  → Integration
 * - vision.echostar.com     → Production
 */

let cachedConfig = null;

// Environment configurations
const OKTA_CONFIGS = {
  // Development environment
  visiondev: {
    clientId: '0oa18bph4bbwxh1Gw2p8',
    issuer: 'https://dish.okta.com/oauth2/aus18cezfnrMzbgp32p8',
    redirectUri: 'https://visiondev.echostar.com/callback',
    postLogoutRedirectUri: 'https://visiondev.echostar.com',
    scopes: ['openid', 'profile', 'email', 'groups', 'offline_access'],
    pkce: true,
    responseType: 'code'
  },
  // Test environment
  visiontest: {
    clientId: '0oa18bpkei2tFfH7i2p8',
    issuer: 'https://dish.okta.com/oauth2/aus18cezfnrMzbgp32p8',
    redirectUri: 'https://visiontest.echostar.com/callback',
    postLogoutRedirectUri: 'https://visiontest.echostar.com',
    scopes: ['openid', 'profile', 'email', 'groups', 'offline_access'],
    pkce: true,
    responseType: 'code'
  },
  // Integration environment
  visionint: {
    clientId: '0oa18899ekpA91F1P2p8',
    issuer: 'https://dish.okta.com/oauth2/aus18cezfnrMzbgp32p8',
    redirectUri: 'https://visionint.echostar.com/callback',
    postLogoutRedirectUri: 'https://visionint.echostar.com',
    scopes: ['openid', 'profile', 'email', 'groups', 'offline_access'],
    pkce: true,
    responseType: 'code'
  },
  // Production environment
  vision: {
    clientId: '0oa188982x9qH5lzH2p8',
    issuer: 'https://dish.okta.com/oauth2/aus18cezfnrMzbgp32p8',
    redirectUri: 'https://vision.echostar.com/callback',
    postLogoutRedirectUri: 'https://vision.echostar.com',
    // offline_access is intentionally omitted for production until the prod Okta
    // app (0oa188982x9qH5lzH2p8) is confirmed to grant the refresh_token type.
    // Requesting an ungranted scope makes /authorize fail with invalid_scope,
    // which would break login for all production users. Verified working on the
    // test app; add it here once prod is confirmed.
    scopes: ['openid', 'profile', 'email', 'groups'],
    pkce: true,
    responseType: 'code'
  },
  // Local development (localhost)
  localhost: {
    clientId: '0oa18bph4bbwxh1Gw2p8',  // Uses dev client
    issuer: 'https://dish.okta.com/oauth2/aus18cezfnrMzbgp32p8',
    redirectUri: 'http://localhost:3000/callback',
    postLogoutRedirectUri: 'http://localhost:3000',
    scopes: ['openid', 'profile', 'email', 'groups', 'offline_access'],
    pkce: true,
    responseType: 'code'
  }
};

/**
 * Detect the current environment from hostname
 * @returns {string} Environment name (visiondev, visiontest, visionint, vision, localhost)
 */
export const detectEnvironment = () => {
  const hostname = window.location.hostname.toLowerCase();
  console.log(`[detectEnvironment] Hostname: ${hostname}`);
  
  if (hostname.includes('visiontest')) return 'visiontest';
  if (hostname.includes('visiondev')) return 'visiondev';
  if (hostname.includes('visionint')) return 'visionint';
  if (hostname === 'vision.echostar.com') return 'vision';
  if (hostname === 'localhost' || hostname === '127.0.0.1') return 'localhost';
  
  // Default to dev for unknown environments (CloudFront URLs, etc.)
  console.warn(`[detectEnvironment] Unknown hostname: ${hostname}, defaulting to visiondev`);
  return 'visiondev';
};

/**
 * Get the current environment name
 * @returns {string} Environment name
 */
export const getEnvironment = () => {
  return detectEnvironment();
};

/**
 * Get Okta configuration for the current environment
 * @returns {Promise<Object>} Okta configuration object
 */
export const getOktaConfig = async () => {
  if (cachedConfig) {
    return cachedConfig;
  }

  const env = detectEnvironment();
  cachedConfig = OKTA_CONFIGS[env];
  
  console.log(`[getOktaConfig] Environment: ${env}`);
  console.log(`[getOktaConfig] Client ID: ${cachedConfig.clientId}`);
  console.log(`[getOktaConfig] Redirect URI: ${cachedConfig.redirectUri}`);
  
  return cachedConfig;
};

/**
 * Get Okta configuration synchronously (for cases where async isn't possible)
 * @returns {Object} Okta configuration object
 */
export const getOktaConfigSync = () => {
  if (cachedConfig) {
    return cachedConfig;
  }

  const env = detectEnvironment();
  cachedConfig = OKTA_CONFIGS[env];
  
  return cachedConfig;
};

/**
 * Clear cached config (useful for testing)
 */
export const clearConfigCache = () => {
  cachedConfig = null;
};

export default {
  getOktaConfig,
  getOktaConfigSync,
  getEnvironment,
  detectEnvironment,
  clearConfigCache
};
